// onboardingReady.js — FLY'S READY SCREEN, the one welcome a person who signed
// up through Fly meets (Nika 2026-10-25, "Short ready screen (Recommended)":
// skip the steps Fly already answered — industry, idea, company — show one
// "Your workspace is ready" screen with the tour offer, and mark the welcome
// done only after a welcome really showed).
//
// WHO: Fly's record — `user_preferences.onboarding = { v: 1, status:
// 'pending', via: 'fly', at }`, written by Make it real where the person had no
// onboarding record (functions/onboarding.js onboardingEligible, reason
// 'fly-ready'). Any other record is a decision and shows nothing.
//
// WHAT: ONE step of the welcome wizard's own surface (components/
// OnboardingWizard.js — its shell, its studio, its title and lead, the
// OnboardingWay tour row and the OnboardingSetupLine rows; components/
// OnboardingReadyStep.js is the step). It says the workspace is ready, lists
// what Fly made — the workspace's apps (the launcher's custom tiles) and its
// website project, the facts the Fly start screen's "Get started" block reads
// (functions/assistantFlyFirstRun.js) — and offers the quick tour and a plain
// close. It asks nothing Fly asked already.
//
// WHEN IT WRITES: `done` only after the screen really showed — `via: 'fly'` on
// close (Escape or Start working: closeOnboarding → onboardingCloseDecision),
// `via: 'tour'` when the tour starts (onboardingChoose) — through the same
// single-flight onboardingDecide every welcome decision uses. A replay never
// writes.
//
// THE BUILD KEEPS RUNNING: the screen opens over the route Fly handed over to.
// On the app builder (`/add-app?prompt=<idea>&fly=`) the idea's first build is
// sent by the builder itself (functions/addAppPrompt.js) and nothing here
// touches it; the page behind is inert, never gone, and closing the screen
// shows the build thread.
//
// NO LAYOUT SHIFT (Rule 66): the facts are read BEFORE the surface opens and
// frozen into the flow (`flow.made`) — a row that landed later would move a
// screen the person is reading.
//
// FRANK: every export is self-contained (FA204) — the caps and the waits are
// restated where they are used; siblings are reached through `this.call`.

// The facts the screen shows, from the root as it is now:
//   apps  the workspace's own apps — the launcher's custom tiles (the house
//         apps every workspace has are not "what Fly made"), each named as the
//         App Store names it
//   site  the newest website project of this workspace, if any
// Titles stay as the tile carries them (a bundle key or the customer's own
// words — onboardingReadyView resolves them through polyglot at render, so a
// language switch is followed); a website project with no name is the bundle
// key of the default name.  → { apps: [{ title, icon }], site: { title } | null }
export function onboardingReadyMade(root) {
  const el = this
  const r = root || (el && el.state && el.state.root) || null
  const fns = (el && el.context && el.context.functions) || {}
  const apps = []
  if (r && typeof fns.visibleStarterTiles === 'function') {
    for (const tile of el.call('visibleStarterTiles', r) || []) {
      if (!tile || !tile.custom || !tile.route) continue
      // An installed app's tile (it carries `appKey`) reads its App Store name —
      // "app names = what a person sees in the workspace App Store" (Nika
      // 2026-10-23), through the same resolver every shell surface uses
      // (functions/installedAppName.js); a customer's own tile, or a shell
      // without that resolver, keeps the tile's own words.
      const named =
        tile.appKey && typeof fns.installedAppName === 'function'
          ? String(
              el.call('installedAppName', { appKey: tile.appKey, title: tile.title }) || ''
            ).trim()
          : ''
      const title = named || String(tile.title || '').trim()
      if (!title) continue
      apps.push({ title, icon: String(tile.icon || '') })
    }
  }
  let site = null
  if (r && typeof fns.assistantFlyFirstRunSites === 'function') {
    const first = (el.call('assistantFlyFirstRunSites', r) || [])[0]
    // a project with no name of its own reads as the default name ("Website"),
    // never as its slug
    if (first) {
      site = {
        title: String(first.name || '').trim() || 'workspace.onboarding.ready.siteDefault'
      }
    }
  }
  // THE IDEA (persona round 5, aiko: "my idea was not on it … so I feared it
  // was lost"): the app the person asked Fly for, whose first build runs
  // behind this screen. Read where it waits or went, first answer wins: the
  // setup thread's plan note (`concepts[0].text`), the text parked for the
  // builder (this tab's `symbols_add_app_prompt`, or the root's hold where
  // storage is refused), the idea Fly held on the workspace
  // (`settings.pendingIdea`), else the first message the person sent in the
  // build thread. → '' when there is none.
  const ideaOf = () => {
    const text = (v) => (typeof v === 'string' ? v.trim() : '')
    const msgs = r && Array.isArray(r._ax_chatMessages) ? r._ax_chatMessages : []
    const note = msgs.find((m) => m && m.metadata && m.metadata.kind === 'note' && m.metadata.plan)
    const plan = note && typeof note.metadata.plan === 'object' ? note.metadata.plan : null
    const concept = plan && Array.isArray(plan.concepts) ? plan.concepts[0] : null
    if (concept && text(concept.text)) return text(concept.text)
    try {
      const store = typeof window !== 'undefined' && window.sessionStorage
      const parked = store ? JSON.parse(store.getItem('symbols_add_app_prompt') || 'null') : null
      if (parked && text(parked.text)) return text(parked.text)
    } catch (_) {
      /* no storage, or a bad park — the next source answers */
    }
    if (r && text(r.__addAppPromptHeld)) return text(r.__addAppPromptHeld)
    const held = r && r.workspace && r.workspace.settings && r.workspace.settings.pendingIdea
    if (held && held.v === 1 && text(held.text)) return text(held.text)
    const first = msgs.find((m) => m && m.role === 'user' && text(m.text))
    return first ? text(first.text) : ''
  }
  return { apps, site, idea: ideaOf() }
}

// Open the ready screen (the gate's tick calls it for Fly's pending welcome).
// The one fact that is not on the root yet is the workspace's website project
// list: only the home reads it, after it mounts, and the screen can open on
// the app builder. It is read now through the canvas package's own read
// (canvasAnswerRead — it lands on the home's `_projectsForActiveOrg`, so the
// home asks nothing again) for at most `opts.waitMs` (1200 ms), a read that
// never answers included. Then the facts are frozen into the flow and the
// surface opens (openOnboarding 'ready').
// → a promise of true when it opened; false when a welcome was opened or a
// decision made meanwhile (a replay, another path), so nothing is stacked.
export function onboardingReadyOpen(opts) {
  const el = this
  const root = el && el.state && el.state.root
  if (!root || typeof root.update !== 'function') return Promise.resolve(false)
  const o = opts && typeof opts === 'object' ? opts : {}
  const waitMs = typeof o.waitMs === 'number' && o.waitMs >= 0 ? o.waitMs : 1200
  const fns = (el.context && el.context.functions) || {}
  let read = Promise.resolve()
  if (!Array.isArray(root._projectsForActiveOrg) && typeof fns.canvasAnswerRead === 'function') {
    try {
      read = Promise.resolve(el.call('canvasAnswerRead', el)).catch(() => null)
    } catch (_) {
      read = Promise.resolve()
    }
  }
  let timer = null
  const cap = new Promise((resolve) => {
    timer = setTimeout(resolve, waitMs)
  })
  // The gate cleared the hold's own timer when it found the person eligible,
  // so a screen that does not open must let the hold's surface go itself.
  const letGo = () => {
    if (root._onboardingHold && !root._onboardingFlow) {
      root.update({ _onboardingHold: false }, { preventFetch: true })
    }
    return false
  }
  return Promise.race([read, cap])
    .then(() => {
      if (timer) clearTimeout(timer)
      if (root._onboardingFlow || root._onboardingDecision) return letGo()
      const opened = el.call('openOnboarding', 'ready', {
        made: el.call('onboardingReadyMade', root)
      })
      return opened ? true : letGo()
    })
    .catch((err) => {
      console.warn('[onboarding] the ready screen did not open:', err && err.message)
      return letGo()
    })
}

// What the step draws: the frozen facts as the setup lines' own rows —
// { show, rows: [row], moreText } with a row = { key, id, title, icon, state,
// done, failed, stateKey }, a name and the plain tick. The apps come first
// (six at most — the presets carry five or six), then the website project, in
// ONE list so the rows line up; a quiet "N more apps" stands under it past the
// six. Every row carries every key a line reads (a childrenAs-'state' child
// falls back to the root's keys for a missing one), and a key is positional —
// never a name that could be a tag.
export function onboardingReadyView(root) {
  const el = this
  const r = root || (el && el.state && el.state.root) || null
  const flow = r && r._onboardingFlow
  const made = flow && flow.made && typeof flow.made === 'object' ? flow.made : null
  const CAP = 6
  const word = (k) => String(el.call('polyglot', k) || '')
  // a row is a name and the plain tick: a state WORD beside it would squeeze
  // the name in a half-width cell (Georgian sets 15-40% longer) and says
  // nothing the caption does not
  const row = (key, title, icon) => ({
    key,
    id: key,
    title: String(el.call('polyglot', title) || title || ''),
    icon,
    state: 'installed',
    done: true,
    failed: false,
    stateKey: ''
  })
  const list = made && Array.isArray(made.apps) ? made.apps : []
  const rows = list
    .slice(0, CAP)
    .map((a, i) => row('ready-app-' + (i + 1), a.title, String(a.icon || '') || 'blocks'))
  // The person's idea first — the person's own words (never a bundle key),
  // on one short line, and "Building now" where the tick would be: its first
  // build runs behind this screen.
  const idea = made && typeof made.idea === 'string' ? made.idea.trim().replace(/\s+/g, ' ') : ''
  if (idea) {
    const MAX = 90
    rows.unshift({
      key: 'ready-idea-1',
      id: 'ready-idea-1',
      title: idea.length > MAX ? idea.slice(0, MAX - 1).trimEnd() + '…' : idea,
      icon: 'magicstar',
      state: 'building',
      done: false,
      failed: false,
      stateKey: 'workspace.onboarding.ready.ideaBuilding'
    })
  }
  if (made && made.site && made.site.title) rows.push(row('ready-site-1', made.site.title, 'globe'))
  const extra = list.length - CAP
  const moreText =
    extra > 0
      ? extra === 1
        ? word('workspace.onboarding.ready.moreOne')
        : word('workspace.onboarding.ready.more').replace('{count}', String(extra))
      : ''
  return { show: rows.length > 0, rows, moreText }
}
